import React, { useEffect, useMemo } from 'react'
import { Typography } from '@mui/material'
import { ResponsiveContainer, PieChart, Pie, Cell, Tooltip as RechartsTooltip } from 'recharts'
import PhoneIcon from '@mui/icons-material/Phone'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import PersonAddIcon from '@mui/icons-material/PersonAdd'
import TrendingUpIcon from '@mui/icons-material/TrendingUp'
import ThumbUpAltIcon from '@mui/icons-material/ThumbUpAlt'
import PieChartIcon from '@mui/icons-material/PieChart'
import SpeedIcon from '@mui/icons-material/Speed'
import BarChartIcon from '@mui/icons-material/BarChart'
import DataUsageIcon from '@mui/icons-material/DataUsage'
import DateRangeInput from '../../components/DateRange/DateRangeInput'
import { fetchChartData, fetchMetrics } from '../../services/bff'
import Skeleton from 'react-loading-skeleton'
import 'react-loading-skeleton/dist/skeleton.css'

import {
  EnhancedMetricsCharts,
  PageLayout,
  PageHeader
} from '../../components'

import MetricCard from '../../components/Metrics/MetricCard'
import { getMonthStart, getCurrentDate, toExclusiveEndDateISO } from '../../services/date'
import moment from 'moment-timezone'
import { dateFormats } from '../../consts'
import { LoadingSpinner } from '../../components/atoms'
import { chartConfig } from '../../consts'
import styles from './Metrics.module.scss'
import useAuthz from '../../hooks/useAuthz'

const COLORS = chartConfig.colors

const AnalyticsMetricsPage = ({ company }) => {
  const { isAdmin } = useAuthz()
  const monthStart = useMemo(() => getMonthStart(), [])
  const now = useMemo(() => getCurrentDate(), [])
  const campaignFilter = isAdmin ? null : company

  const [metrics, setMetrics] = React.useState(null)
  const [metricsLoading, setMetricsLoading] = React.useState(false)
  const [chartData, setChartData] = React.useState({
    callsPerDay: [],
    handleTimePerDay: [],
    cssPerDay: [],
    dispositionBreakdown: [],
    callDirection: []
  })
  const [chartLoading, setChartLoading] = React.useState(false)

  const [chartDateRange, setChartDateRange] = React.useState(() => ({
    startDate: getMonthStart(),
    endDate: getCurrentDate()
  }))

  useEffect(() => {
    if (!(chartDateRange.startDate instanceof Date) || isNaN(chartDateRange.startDate.getTime()) ||
        !(chartDateRange.endDate instanceof Date) || isNaN(chartDateRange.endDate.getTime())) {
      return
    }
    let cancelled = false
    const fetchData = async () => {
      // Start of selected day in business TZ; exclusive end = start of next day.
      // Without the +1 day, the end-date's own calls get excluded (so today's
      // calls never appear on the chart) — that's the bug that caused the
      // daily-count stat card to disagree with the chart total.
      const startStr = moment(chartDateRange.startDate).tz(dateFormats.timezone).startOf('day').toDate().toISOString()
      const endStr = toExclusiveEndDateISO(chartDateRange.endDate)
      setChartLoading(true)
      try {
        const data = await fetchChartData(startStr, endStr, campaignFilter)
        if (!cancelled) setChartData(data)
      } catch (error) {
        console.error('Error fetching chart data:', error)
      } finally {
        if (!cancelled) setChartLoading(false)
      }
    }

    fetchData()
    return () => { cancelled = true }
  }, [chartDateRange, campaignFilter])

  useEffect(() => {
    let cancelled = false
    if (isNaN(monthStart) || isNaN(now)) {
      setMetricsLoading(false)
      return
    }
    const startStr = monthStart.toISOString().split('T')[0]
    const endStr = now.toISOString().split('T')[0]

    const getMetrics = async () => {
      setMetricsLoading(true)
      try {
        const metricsData = await fetchMetrics(startStr, endStr, campaignFilter)
        if (cancelled) return
        setMetrics(metricsData)
      } catch (error) {
        console.error('Error in getMetrics:', error)
        if (!cancelled) setMetrics(null)
      } finally {
        if (!cancelled) setMetricsLoading(false)
      }
    }

    getMetrics()
    return () => { cancelled = true }
  }, [campaignFilter, monthStart, now])

  const handleChartStartDateChange = (event) => {
    const value = event?.target?.value || event
    const parsed = new Date(value + 'T12:00:00')
    if (!isNaN(parsed.getTime())) {
      setChartDateRange(prev => ({ ...prev, startDate: parsed }))
    }
  }

  const handleChartEndDateChange = (event) => {
    const value = event?.target?.value || event
    const parsed = new Date(value + 'T12:00:00')
    if (!isNaN(parsed.getTime())) {
      setChartDateRange(prev => ({ ...prev, endDate: parsed }))
    }
  }

  const processCallDirectionData = () => {
    const safeCallDirectionData = chartData?.callDirection || []
    let inboundTotal = 0
    let outboundTotal = 0

    safeCallDirectionData.forEach((item) => {
      if (item.name === 'Manual') {
        outboundTotal += item.value
      } else {
        inboundTotal += item.value
      }
    })

    const result = []
    if (inboundTotal > 0) result.push({ name: 'Inbound', value: inboundTotal })
    if (outboundTotal > 0) result.push({ name: 'Outbound', value: outboundTotal })
    return result
  }

  const processDispositionData = () => {
    const raw = chartData?.dispositionBreakdown || []
    if (raw.length <= 6) return raw

    const sorted = [...raw].sort((a, b) => b.value - a.value)
    const top5 = sorted.slice(0, 5)
    const otherTotal = sorted.slice(5).reduce((sum, item) => sum + item.value, 0)
    if (otherTotal > 0) {
      top5.push({ name: 'Other', value: otherTotal })
    }
    return top5
  }

  const hasPieChartData = chartData?.callDirection &&
    Array.isArray(chartData.callDirection) &&
    chartData.callDirection.length > 0

  const todayLabel = getCurrentDate().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  })

  return (
    <PageLayout>
      <PageHeader
        title='Impact Overview'
        subtitle='Key performance metrics and call analytics at a glance'
        className='animate-in animate-in-1'
      />

      {metricsLoading || !metrics || metrics?.dailyCallCount === undefined
        ? (
          <div className={styles.loadingContainer}>
            <LoadingSpinner size={60} />
          </div>
          )
        : (
          <div className={`${styles.contentWrapper} content-loaded`}>
            {/* Today&#39;s Metrics */}
            <section className='animate-in animate-in-2'>
              <div className={styles.sectionLabel}>
                <h2 className={styles.sectionTitle}>Today&#39;s Metrics</h2>
                <span className={styles.sectionDate}>{todayLabel}</span>
              </div>

              <div className={styles.metricsGrid}>
                <MetricCard
                  icon={PhoneIcon}
                  value={metrics.dailyCallCount ?? 0}
                  label='Daily Call Count'
                  description='Total number of calls handled today'
                  type='daily'
                  className='metric-card'
                  prevValue={metrics.prevDayCallCount}
                  avgValue={metrics.avgDailyCalls}
                  workdaysLabel={metrics.workdaysLabel}
                />
                <MetricCard
                  icon={AccessTimeIcon}
                  value={metrics.dailyWorkMinutes ?? 0}
                  label='Daily Call Minutes'
                  description='Total handle time in minutes today'
                  type='daily'
                  className='metric-card'
                  prevValue={metrics.prevDayWorkMinutes}
                  avgValue={metrics.avgDailyMinutes}
                  workdaysLabel={metrics.workdaysLabel}
                />
                <MetricCard
                  icon={PersonAddIcon}
                  value={metrics.hotLeads ?? 0}
                  label='Potential New Clients'
                  description='Hot leads identified today'
                  type='daily'
                  className='metric-card'
                  prevValue={metrics.prevDayHotLeads}
                  avgValue={metrics.avgDailyHotLeads}
                  workdaysLabel={metrics.workdaysLabel}
                />
                <MetricCard
                  icon={ThumbUpAltIcon}
                  value={metrics.cssMonthToDate ?? 0}
                  label='Customer Satisfaction'
                  description='CSS score for the current month (0-100)'
                  type='daily'
                  decimals={1}
                  className='metric-card'
                />
              </div>
            </section>

            {/* Month-to-Date Summary */}
            <section className='animate-in animate-in-3'>
              <div className={styles.sectionLabel}>
                <h2 className={styles.sectionTitle}>Month-to-Date</h2>
                <span className={styles.sectionDate}>
                  {monthStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} &mdash; {todayLabel}
                </span>
              </div>

              <div className={styles.metricsGrid}>
                <MetricCard
                  icon={PhoneIcon}
                  value={metrics.callCount ?? 0}
                  label='Total Calls'
                  description='All calls handled this month'
                  type='monthToDate'
                  className='metric-card'
                />
                <MetricCard
                  icon={AccessTimeIcon}
                  value={metrics.workMinutes ?? 0}
                  label='Total Minutes'
                  description='Total handle time this month'
                  type='monthToDate'
                  decimals={1}
                  className='metric-card'
                />
                <MetricCard
                  icon={PieChartIcon}
                  value={metrics.callCount > 0 ? ((metrics.dateRangeHotLeads / metrics.callCount) * 100) : 0}
                  label='Conversion Rate'
                  description='Hot leads as a percentage of total calls'
                  type='monthToDate'
                  decimals={1}
                  isPercentage
                  className='metric-card'
                />
                <MetricCard
                  icon={SpeedIcon}
                  value={metrics.callCount > 0 ? (metrics.workMinutes / metrics.callCount) : 0}
                  label='Avg Handle Time'
                  description='Average minutes per call this month'
                  type='monthToDate'
                  decimals={1}
                  className='metric-card'
                  additionalInfo='min/call'
                />
                {metrics.planMinutes && (
                  <MetricCard
                    icon={DataUsageIcon}
                    value={metrics.planUsePercentage ?? 0}
                    label='Plan Usage'
                    description={`${Math.round(metrics.planRemainingMinutes ?? 0)} minutes remaining of ${metrics.planMinutes} allocated`}
                    type='monthToDate'
                    decimals={1}
                    isPercentage
                    className='metric-card'
                  />
                )}
              </div>
            </section>

            {/* Charts Section */}
            <section className='animate-in animate-in-4'>
              <div className={styles.sectionLabel}>
                <h2 className={styles.sectionTitle}>Charts &amp; Trends</h2>
              </div>

              <DateRangeInput
                startDate={chartDateRange.startDate}
                endDate={chartDateRange.endDate}
                onStartDateChange={handleChartStartDateChange}
                onEndDateChange={handleChartEndDateChange}
                disabled={false}
              />
            </section>

            {/* Chart grid */}
            <div className={`${styles.chartGrid} animate-in animate-in-5`}>
              <div className={styles.lineChartsColumn}>
                <EnhancedMetricsCharts
                  lineChartData={{
                    callsPerDay: chartData.callsPerDay,
                    handleTimePerDay: chartData.handleTimePerDay,
                    cssPerDay: chartData.cssPerDay
                  }}
                  metricsLoading={metricsLoading}
                  chartLoading={chartLoading}
                />
              </div>

              {/* Pie charts row */}
              <div className={styles.pieChartsRow}>
                {/* Call Direction pie chart */}
                <div className={styles.pieChartCard}>
                  <div className={styles.pieChartHeader}>
                    <TrendingUpIcon className={styles.pieChartIcon} />
                    <span className={styles.pieChartTitle}>Call Direction</span>
                  </div>

                  <div className={styles.pieChartBody}>
                    {chartLoading
                      ? (
                        <div className={styles.pieChartLoading}>
                          <Skeleton height={200} className={styles.skeletonChart} />
                          <Skeleton height={20} width='80%' className={styles.skeletonChart} />
                        </div>
                        )
                      : !hasPieChartData
                        ? (
                          <div className={styles.emptyState}>
                            <Typography variant='body2' className={styles.emptyStateText}>
                              No call direction data for selected range.
                            </Typography>
                          </div>
                          )
                        : (
                          <>
                            <div role='img' aria-label='Call Direction Distribution chart'>
                              <ResponsiveContainer width='100%' height={220}>
                                <PieChart>
                                  <Pie
                                    data={processCallDirectionData()}
                                    dataKey='value'
                                    nameKey='name'
                                    cx='50%'
                                    cy='50%'
                                    outerRadius={85}
                                    innerRadius={45}
                                  >
                                    {processCallDirectionData().map((entry, index) => (
                                      <Cell
                                        key={`cell-${index}`}
                                        fill={COLORS[index % COLORS.length]}
                                        stroke='var(--color-bg-white)'
                                        strokeWidth={2}
                                      />
                                    ))}
                                  </Pie>
                                  <RechartsTooltip formatter={(value, name) => [`${value} calls`, name]} />
                                </PieChart>
                              </ResponsiveContainer>
                            </div>

                            <div className={styles.pieChartLegend}>
                              {processCallDirectionData().map((entry, index) => {
                                const total = processCallDirectionData().reduce((sum, item) => sum + item.value, 0)
                                return (
                                  <div key={entry.name} className={styles.legendItem}>
                                    <div
                                      className={styles.legendDot}
                                      style={{ backgroundColor: COLORS[index % COLORS.length] }}
                                    />
                                    <span className={styles.legendText}>
                                      {entry.name}: {entry.value} ({((entry.value / total) * 100).toFixed(0)}%)
                                    </span>
                                  </div>
                                )
                              })}
                            </div>
                          </>
                          )}
                  </div>
                </div>

                {/* Disposition Breakdown pie chart */}
                <div className={styles.pieChartCard}>
                  <div className={styles.pieChartHeader}>
                    <BarChartIcon className={styles.pieChartIcon} />
                    <span className={styles.pieChartTitle}>Disposition Breakdown</span>
                  </div>

                  <div className={styles.pieChartBody}>
                    {chartLoading
                      ? (
                        <div className={styles.pieChartLoading}>
                          <Skeleton height={200} className={styles.skeletonChart} />
                          <Skeleton height={20} width='80%' className={styles.skeletonChart} />
                        </div>
                        )
                      : !chartData?.dispositionBreakdown?.length
                        ? (
                          <div className={styles.emptyState}>
                            <Typography variant='body2' className={styles.emptyStateText}>
                              No disposition data for selected range.
                            </Typography>
                          </div>
                          )
                        : (
                          <>
                            <div role='img' aria-label='Disposition Breakdown chart'>
                              <ResponsiveContainer width='100%' height={220}>
                                <PieChart>
                                  <Pie
                                    data={processDispositionData()}
                                    dataKey='value'
                                    nameKey='name'
                                    cx='50%'
                                    cy='50%'
                                    outerRadius={85}
                                    innerRadius={45}
                                  >
                                    {processDispositionData().map((entry, index) => (
                                      <Cell
                                        key={`disp-${index}`}
                                        fill={COLORS[index % COLORS.length]}
                                        stroke='var(--color-bg-white)'
                                        strokeWidth={2}
                                      />
                                    ))}
                                  </Pie>
                                  <RechartsTooltip formatter={(value, name) => [`${value} calls`, name]} />
                                </PieChart>
                              </ResponsiveContainer>
                            </div>

                            <div className={styles.pieChartLegend}>
                              {processDispositionData().map((entry, index) => {
                                const total = processDispositionData().reduce((sum, item) => sum + item.value, 0)
                                return (
                                  <div key={entry.name} className={styles.legendItem}>
                                    <div
                                      className={styles.legendDot}
                                      style={{ backgroundColor: COLORS[index % COLORS.length] }}
                                    />
                                    <span className={styles.legendText}>
                                      {entry.name}: {entry.value} ({((entry.value / total) * 100).toFixed(0)}%)
                                    </span>
                                  </div>
                                )
                              })}
                            </div>
                          </>
                          )}
                  </div>
                </div>
              </div>
            </div>
          </div>
          )}
    </PageLayout>
  )
}

export default AnalyticsMetricsPage
